import React from "react";
import {
  Box,
  Button,
  MobileStepper,
  Skeleton,
  Step,
  StepButton,
  Stepper,
  Typography,
} from "@mui/material";
import { useFormOrchestrator } from "../FormContext/useFormContext";
import { ArrowBackIos } from "@mui/icons-material";
import { ArrowForwardIos } from "@mui/icons-material";
import { FormTypes } from "../../../types/PatientFormTypes";
import { getIsMobile } from "../../../utils/helpers";

const purpleStepStyle = {
  '& .MuiStepIcon-root:not(.Mui-active)': {
    color: 'secondary.main',
  },
  '& .MuiStepIcon-text': {
    fill: 'secondary.primaryContrast',
  }
}

const goldStepStyle = {
  '& .MuiStepIcon-root:not(.Mui-active)': {
    color: 'warning.main',
  },
  '& .MuiStepIcon-text': {
    fill: 'secondary.primaryContrast',
  }
}

/**
 * FormProgress Component
 * 
 * This component displays the progress through a multi-step form.
 * It shows the current step, completed steps, and allows navigation between steps.
 */
const FormProgress: React.FC = () => {
  const { formState, formConfig, goToStep, isStepCompleted } = useFormOrchestrator();
  const isMobile = getIsMobile();
  const allowSkip = formConfig.allowSkipSteps;
  const steps = formConfig.steps;
  
  if (formState.isLoading) return (
    // Skeleton form of the form progress
    <Box sx={{ width: '100%', mb: 1 }}>
      {isMobile ? (
        <MobileStepper
          variant="dots"
          steps={steps.length}
          activeStep={formState.currentStepIndex}
          position="static"
          backButton={<Button disabled><ArrowBackIos sx={{ color: 'action.disabled' }} /> Back</Button>}
          nextButton={<Button disabled>Next <ArrowForwardIos sx={{ color: 'action.disabled' }} /></Button>}
        />
      ) : (
        <Stepper 
          activeStep={formState.currentStepIndex} 
          alternativeLabel
          sx={{ mb: 2 }}
        >
          {steps.map((step, index) => (
            <Step key={index} disabled>
              <StepButton>{step.abbreviation || step.title}</StepButton>
            </Step>
          ))}
        </Stepper>
      )}
    </Box>
  );

  if (isMobile) {
    const forwardDisabled = !isStepCompleted(steps[formState.currentStepIndex]) && !allowSkip;
    const backDisabled = formState.currentStepIndex === 0;
    return (
      <Box sx={{ width: '100%', mb: 1 }}>
        <MobileStepper
          variant="dots"
          steps={steps.length}
          position="static"
          activeStep={formState.currentStepIndex}
          sx={{ 
            flexGrow: 1, 
            bgcolor: 'transparent',
            '& .MuiLinearProgress-root': {
              width: '100%'
            }
          }}
          nextButton={
            <Button 
              onClick={() => goToStep(formState.currentStepIndex + 1)} 
              disabled={forwardDisabled}
            >
              Next
              <ArrowForwardIos sx={{ color: forwardDisabled ? 'action.disabled' : 'primary.main' }} />
            </Button>
          }
          backButton={
            <Button 
              onClick={() => goToStep(formState.currentStepIndex - 1)} 
              disabled={backDisabled}
            >
              <ArrowBackIos sx={{ color: backDisabled ? 'action.disabled' : 'primary.main' }} />
              Back
            </Button>
          }
        />
        <Typography 
          variant="h5" 
          align="center" 
          sx={{ fontWeight: 'medium' }}
        >
          {steps[formState.currentStepIndex].title}
        </Typography>
      </Box>
    );
  }

  return (
    <Stepper 
      activeStep={formState.currentStepIndex} 
      alternativeLabel
      nonLinear
      sx={{ mb: 2 }}
    >
      {steps.map((step, index) => {
        const stepCompleted = !allowSkip && formState.completedSteps.includes(step.id);
        const stepDisabled = !allowSkip && !stepCompleted && step.id !== formState.progressStep;
        return (
          <Step 
            key={index}
            disabled={stepDisabled && !allowSkip}
            completed={stepCompleted && formState.currentStepIndex !== index}
            sx={allowSkip 
              ? purpleStepStyle 
              : (!stepCompleted && !stepDisabled) 
                ? goldStepStyle 
                : {}}
          >
            <StepButton onClick={() => {
              goToStep(index);
            }}>
              {step.abbreviation || step.title}
            </StepButton>
          </Step>
        )
      })} 
    </Stepper>
  );
};

export default FormProgress;
